import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  cancelEmailCampaign,
  exportEmailCampaigns,
  listEmailCampaigns,
  pauseEmailCampaign,
  sendEmailCampaign,
} from '../../../api/adminEmailCenter';
import EmailCenterPage, { EmptyState, PageSection, StatusBadge, downloadCsv, formatDateTime, TextField } from './EmailCenterShared';

export default function EmailCampaigns() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [state, setState] = useState({ loading: true, error: '', items: [], pagination: { page: 1, totalPages: 1 } });
  const [busyId, setBusyId] = useState(null);

  const load = async (page = 1) => {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const data = await listEmailCampaigns({ page, limit: 20, ...filters });
      setState({ loading: false, error: '', items: data.items || [], pagination: data.pagination || { page: 1, totalPages: 1 } });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message || 'Failed to load campaigns' }));
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    return state.items.reduce(
      (acc, item) => {
        acc.sent += Number(item.sentCount || 0);
        acc.failed += Number(item.failedCount || 0);
        acc.queued += Number(item.queuedCount || 0);
        return acc;
      },
      { sent: 0, failed: 0, queued: 0 }
    );
  }, [state.items]);

  const handleExport = async () => {
    const csv = await exportEmailCampaigns();
    downloadCsv(csv, `email-campaigns-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const handleAction = async (campaignId, action) => {
    setBusyId(campaignId);
    try {
      if (action === 'send') await sendEmailCampaign(campaignId);
      if (action === 'pause') await pauseEmailCampaign(campaignId);
      if (action === 'cancel') await cancelEmailCampaign(campaignId);
      await load(state.pagination.page || 1);
    } catch (error) {
      setState((current) => ({ ...current, error: error.message || 'Campaign action failed' }));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <EmailCenterPage
      title="Campaign Studio"
      description="Create, schedule, pause, and relaunch promotional campaigns while keeping audience size and queue health visible."
      actions={
        <>
          <button type="button" onClick={handleExport} className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700">
            Export
          </button>
          <Link to="/admin/email/campaigns/new" className="rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_32px_rgba(15,118,110,0.24)]">
            New Campaign
          </Link>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Sent</p>
          <div className="mt-4 text-3xl font-semibold text-slate-900">{totals.sent}</div>
        </div>
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Failed</p>
          <div className="mt-4 text-3xl font-semibold text-slate-900">{totals.failed}</div>
        </div>
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Queued</p>
          <div className="mt-4 text-3xl font-semibold text-slate-900">{totals.queued}</div>
        </div>
      </div>

      {state.error ? <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{state.error}</div> : null}

      <PageSection
        title="Campaign List"
        subtitle="Every campaign stays auditable with recipient counts, status, and direct queue actions."
        action={
          <div className="flex flex-col gap-3 md:flex-row">
            <TextField
              label="Search"
              value={filters.search}
              onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
              placeholder="Search campaigns"
            />
            <label className="block">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Status</span>
              <select
                value={filters.status}
                onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value }))}
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900"
              >
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="paused">Paused</option>
                <option value="cancelled">Cancelled</option>
                <option value="failed">Failed</option>
              </select>
            </label>
            <div className="self-end">
              <button type="button" onClick={() => load(1)} className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white">
                Apply
              </button>
            </div>
          </div>
        }
      >
        {!state.items.length && !state.loading ? (
          <EmptyState title="No campaigns found" body="Start with a draft campaign, test the content, then queue it from here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.24em] text-slate-500">
                <tr>
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Recipients</th>
                  <th className="px-4 py-3">Progress</th>
                  <th className="px-4 py-3">Schedule</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {state.items.map((item) => (
                  <tr key={item.id} className="border-t border-slate-100">
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-semibold text-slate-900">{item.campaignName}</p>
                        <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">{item.campaignType}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge value={item.status} />
                    </td>
                    <td className="px-4 py-4 text-slate-600">{item.totalRecipients}</td>
                    <td className="px-4 py-4 text-slate-600">
                      sent {item.sentCount} | failed {item.failedCount} | queued {item.queuedCount}
                    </td>
                    <td className="px-4 py-4 text-slate-600">{formatDateTime(item.scheduledAt)}</td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => navigate(`/admin/email/campaigns/${item.id}`)} className="rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">
                          Edit
                        </button>
                        {['draft', 'scheduled', 'paused', 'failed'].includes(item.status) ? (
                          <button
                            type="button"
                            disabled={busyId === item.id}
                            onClick={() => handleAction(item.id, 'send')}
                            className="rounded-full bg-emerald-600 px-3 py-2 text-xs font-semibold text-white"
                          >
                            {busyId === item.id ? 'Working...' : 'Send'}
                          </button>
                        ) : null}
                        {item.status === 'processing' ? (
                          <button
                            type="button"
                            disabled={busyId === item.id}
                            onClick={() => handleAction(item.id, 'pause')}
                            className="rounded-full border border-amber-200 px-3 py-2 text-xs font-semibold text-amber-700"
                          >
                            Pause
                          </button>
                        ) : null}
                        {!['completed', 'cancelled'].includes(item.status) ? (
                          <button
                            type="button"
                            disabled={busyId === item.id}
                            onClick={() => handleAction(item.id, 'cancel')}
                            className="rounded-full border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700"
                          >
                            Cancel
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageSection>
    </EmailCenterPage>
  );
}
